import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { Order } from "@/api/order/_models/Order";
import { formatDateTime } from "@/app/date.utils";
import { toTitleCase } from "@/app/text.utils";
import { ListGridExpandedFields } from "@/components/list-grid/ListGridExpandedFields";
import type { ListGridExpandedField } from "@/components/list-grid/ListGridExpandedFields";
import { ListGridExpandedSection } from "@/components/list-grid/ListGridExpandedSection";

interface OrderListExpandedDetailProps {
  order: Order;
}

const toTagList = (tags: string[]): ReactNode => {
  if (tags.length === 0) return "—";
  return (
    <Stack
      direction="row"
      sx={{
        flexWrap: "wrap",
        gap: 0.5,
        justifyContent: "flex-end",
      }}
    >
      {tags.map((tag) => (
        <Box
          component="span"
          key={tag}
          sx={{
            bgcolor: "action.selected",
            borderRadius: 0.5,
            color: "text.secondary",
            fontSize: 12,
            fontWeight: 600,
            px: 0.75,
          }}
        >
          {tag}
        </Box>
      ))}
    </Stack>
  );
};

const toDetailFieldList = (order: Order): ListGridExpandedField[] => {
  const customerOrder = order.customerOrder;
  let customerName = "—";
  if (customerOrder?.customerName) customerName = toTitleCase(customerOrder.customerName);

  return [
    { label: "Customer", value: customerName },
    { label: "Retailer order", value: customerOrder?.reference || "—" },
    { label: "Ordered", value: formatDateTime(order.orderDate) },
    { label: "Last synced", value: formatDateTime(order.lastSyncedAt) },
    { label: "Tags", value: toTagList(customerOrder?.customerTags ?? []) },
  ];
};

/** The expanded order's Details section: customer, retailer order, ordered / last synced dates and the customer's tags. */
export const OrderListExpandedDetail = (props: OrderListExpandedDetailProps) => {
  const { order } = props;
  const fields = toDetailFieldList(order);

  return (
    <ListGridExpandedSection title="Details">
      <ListGridExpandedFields fields={fields} />
    </ListGridExpandedSection>
  );
};
